import React, { useContext, useState } from 'react'
import uuid from 'uuid'
import { startOfTomorrow, endOfTomorrow } from 'date-fns'
import { utcToZonedTime } from 'date-fns-tz'

import { AccordionCard } from '../../components/accordion-card'
import { AuditLogTab } from '../../common/auditlog'
import { ColorPicker } from '../../components/color_picker'
import { DatePicker } from '../../common/date'
import { TabsPanelWithLock } from '../../components/tab-panel-with-lock'
import { I18NField } from '../../components/i18nfield'
import {
  useTraversal,
  ContextToolbar,
  PanelPermissions,
  Button,
  Input,
  Icon,
  Checkbox,
} from '@guillotinaweb/react-gmi'
import { formatStartEnd, useClipboard } from '../../utils'
import { LANGS, LANG_NAMES, setLangs } from '../../consts'

import './promos_editor.css'

const Empty = () => null

function defaultPromo() {
  const [date_start, date_end] = formatStartEnd(
    startOfTomorrow(),
    endOfTomorrow()
  )

  return {
    date_start,
    date_end,
    bg_color: '#fbe89d',
    text: setLangs(''),
    link: setLangs(''),
    display_countdown: true,
    isNew: true,
    id: uuid(),
  }
}

function Promos() {
  const [clipboard, setClipboard] = useClipboard('copy-promo')
  const [loading, setLoading] = useState(false)
  const Ctx = useTraversal()
  const [promos, setPromos] = useState(
    Ctx.state.context.promos.map((p) => ({ ...p, id: uuid() }))
  )

  const canModifyContent = Ctx.hasPerm('guillotina.ModifyContent')

  function getMissingTranslation() {
    return promos.reduce((res, promo, i) => {
      const missingLang = LANGS.find((lang) => !promo.text[lang])

      if (!missingLang) return res

      return {
        lang: LANG_NAMES[missingLang],
        title: `"${
          LANGS.map((l) => promo.text[l]).find((v) => v) || `#${i + 1}`
        }"`,
      }
    }, null)
  }

  async function save() {
    if (!canModifyContent) return

    const missingTranslation = getMissingTranslation()

    if (missingTranslation) {
      Ctx.flash(
        `It's missing the ${missingTranslation.lang} translation of the promo ${missingTranslation.title}`,
        'danger'
      )
      return
    }

    setLoading(true)

    const ok = ['Promos saved correctly', 'success']
    const ko = ['There is an error on saving promos', 'danger']
    const { status } = await Ctx.client.patch(Ctx.path, {
      ...Ctx.state.context,
      promos,
    })

    Ctx.flash(...(status === 204 ? ok : ko))
    Ctx.refresh()
    setLoading(false)
    window.scrollTo({ top: 0 })
  }

  function copy(promo) {
    sessionStorage.setItem('copy-promo', JSON.stringify(promo))
    setClipboard(promo)
    Ctx.flash('Copied!', 'success')
  }

  function paste() {
    add(null, { ...clipboard, id: uuid() })
  }

  function add(e, promo = defaultPromo()) {
    setPromos((p) => [promo, ...p])
  }

  function remove(id) {
    setPromos((p) => p.filter((promo) => promo.id !== id))
  }

  function update(promo, value, key) {
    setPromos((p) =>
      p.map((oldPromo) =>
        oldPromo.id === promo.id ? { ...promo, [key]: value } : oldPromo
      )
    )
  }

  const controls = (
    <div className="promos-controls">
      <Button className="is-small" onClick={paste} disabled={!clipboard}>
        <Icon icon="fas fa-paste" />
      </Button>
      <Button className="is-small" onClick={add} disabled={!canModifyContent}>
        Add
      </Button>
      <Button
        disabled={loading || !canModifyContent}
        className="is-small is-primary"
        onClick={save}
      >
        Save all
      </Button>
    </div>
  )

  if (promos.length === 0) {
    return (
      <>
        {controls}
        <div className="no-promos">
          There aren't promos on {Ctx.state.context.title} yet
        </div>
      </>
    )
  }

  return (
    <>
      {controls}
      <div className="promo-list">
        {promos.map((promo) => (
          <AccordionCard
            key={promo.id}
            title={
              <div className="header-title">
                <div>
                  <b>{promo.text.es || '...'}</b>
                  <p>{`${promo.date_start} - ${promo.date_end}`}</p>
                </div>
                <div>
                  <span onClick={() => copy(promo)} title="Copy">
                    <Icon icon="fas fa-copy" />
                  </span>
                  <span onClick={() => remove(promo.id)} title="Delete">
                    <Icon icon="fas fa-trash" />
                  </span>
                </div>
              </div>
            }
            defaultCollapsed={!promo.isNew}
          >
            <div className="top-form">
              <div>
                <DatePicker
                  value={promo.date_start}
                  name="Start date"
                  onUpdate={(e) => update(promo, e.target.value, 'date_start')}
                />
                <DatePicker
                  value={promo.date_end}
                  name="End date"
                  onUpdate={(e) => {
                    let value = null

                    if (e.target.value) {
                      const date = new Date(Date.parse(e.target.value))
                      const zoneDate = utcToZonedTime(date, 'Europe/Madrid')

                      zoneDate.setHours(23, 59, 59)
                      value = zoneDate.toISOString()
                    }

                    update(promo, value, 'date_end')
                  }}
                />
                <Checkbox
                  children=" Display countdown"
                  value={promo.display_countdown}
                  onChange={(v) => update(promo, v, 'display_countdown')}
                />
                <ColorPicker
                  label="Background color"
                  onChange={(e) => update(promo, e.target.value, 'bg_color')}
                  value={promo.bg_color}
                />
              </div>
              <I18NField Widget={Empty}>
                {(lang) => (
                  <>
                    <label className="label">Text:</label>
                    <Input
                      key={`text-${lang}`}
                      placeholder="Text"
                      value={promo.text[lang]}
                      onChange={(v) =>
                        update(promo, { ...promo.text, [lang]: v }, 'text')
                      }
                    />
                    <label className="label">Link:</label>
                    <p class="note-text">
                      ES - https://www.vinissimus.com
                      <strong>/es/promocion/</strong> --> "/es/promocion/"
                      <br></br>
                      EN - https://www.vinissimus.com
                      <strong>/en/promotion/</strong> and <br></br>
                      https://www.vinissimus.co.uk
                      <strong>/en/promotion/</strong> ——> "/en/promotion/"
                      <br></br>
                      FR - https://www.vinissimus.fr
                      <strong>/fr/promotion/</strong> ——> "/fr/promotion/"
                      <br></br>
                      DE - https://www.hispavinus.de<strong>
                        /de/aktion/
                      </strong>{' '}
                      ——> "/de/aktion/"<br></br>
                      IT - https://www.italvinus.it
                      <strong>/it/promozione/</strong> ——> "/it/promozione/"
                      <br></br>
                    </p>
                    <Input
                      key={`link-${lang}`}
                      placeholder="Link"
                      value={promo.link[lang]}
                      onChange={(v) =>
                        update(promo, { ...promo.link, [lang]: v }, 'link')
                      }
                    />
                    <p class="note-text">
                      <strong>⚠ Keep the "/" at the beginning of link</strong>.
                      It does that the link works in any site page (not only on
                      home page).
                    </p>
                  </>
                )}
              </I18NField>
            </div>
          </AccordionCard>
        ))}
      </div>
    </>
  )
}

const tabs = {
  Content: Promos,
  Permissions: PanelPermissions,
  AuditLog: AuditLogTab,
}

const tabsPermissions = {
  Content: 'guillotina.ViewContent',
  Permissions: 'guillotina.SeePermissions',
  AuditLog: 'vinissimus.ViewLogs',
}

export function PromosView(props) {
  const ctx = useTraversal()
  const calculated = ctx.filterTabs(tabs, tabsPermissions)

  return (
    <TabsPanelWithLock
      tabs={calculated}
      currentTab="Content"
      editableTabs={['Content']}
      rightToolbar={<ContextToolbar {...props} />}
      {...props}
    />
  )
}
